'use client';

import React, { useState } from 'react';
import { Plus, Trash2, Tag, X, Check } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslations } from '@/lib/i18n';

interface PriceEntry { productId: string; productName: string; basePrice: number; customPrice: number; }
interface PriceList { id: string; name: string; zone: string; tier: string; products: PriceEntry[]; active: boolean; }

const initialLists: PriceList[] = [
  { id: 'pl1', name: 'Centre Ville — Gros', zone: 'Centre_Ville', tier: 'Grossiste', active: true, products: [{ productId: 'p1', productName: 'Fromage Frais 1kg', basePrice: 45, customPrice: 40 }, { productId: 'p2', productName: 'Yaourt Nature 500g', basePrice: 12, customPrice: 10.5 }] },
  { id: 'pl2', name: 'Ville Haute — Standard', zone: 'Ville_Haute', tier: 'Standard', active: true, products: [{ productId: 'p1', productName: 'Fromage Frais 1kg', basePrice: 45, customPrice: 43 }, { productId: 'p3', productName: 'Lait Entier 1L', basePrice: 8, customPrice: 7.5 }] },
  { id: 'pl3', name: 'Khwazat — VIP', zone: 'Khwazat', tier: 'VIP', active: false, products: [{ productId: 'p2', productName: 'Yaourt Nature 500g', basePrice: 12, customPrice: 9 }] },
];

const zones = ['Centre_Ville', 'Ville_Haute', 'Khwazat'];
const tiers = ['Standard', 'Grossiste', 'VIP', 'Détaillant'];

export default function PriceListsContent() {
  const { language, dir } = useLanguage();
  const t = getTranslations(language);
  const [lists, setLists] = useState<PriceList[]>(initialLists);
  const [showForm, setShowForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newZone, setNewZone] = useState(zones[0]);
  const [newTier, setNewTier] = useState(tiers[0]);
  const [expanded, setExpanded] = useState<string | null>(null);

  const addList = () => {
    if (!newName.trim()) return;
    setLists([...lists, { id: `pl${Date.now()}`, name: newName, zone: newZone, tier: newTier, active: true, products: [] }]);
    setNewName(''); setShowForm(false);
  };

  const toggleActive = (id: string) => setLists(lists.map(l => l.id === id ? { ...l, active: !l.active } : l));
  const deleteList = (id: string) => setLists(lists.filter(l => l.id !== id));

  return (
    <div className="space-y-6 fade-in" dir={dir}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-700 text-foreground">{t('pricelists.title')}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{t('pricelists.subtitle')}</p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary flex-shrink-0">
          <Plus size={16} /> {t('pricelists.add_list')}
        </button>
      </div>

      {showForm && (
        <div className="bg-card border border-border rounded-xl p-5 shadow-card space-y-4">
          <h3 className="font-600 text-foreground">{t('pricelists.add_list')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input className="input-base" placeholder={t('pricelists.list_name')} value={newName} onChange={e => setNewName(e.target.value)} />
            <select className="input-base" value={newZone} onChange={e => setNewZone(e.target.value)}>
              {zones.map(z => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}
            </select>
            <select className="input-base" value={newTier} onChange={e => setNewTier(e.target.value)}>
              {tiers.map(t2 => <option key={t2} value={t2}>{t2}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={addList} className="btn-primary"><Check size={16} /> {t('common.save')}</button>
            <button onClick={() => setShowForm(false)} className="btn-secondary"><X size={16} /> {t('common.cancel')}</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {lists.map((list) => (
          <div key={list.id} className="bg-card border border-border rounded-xl shadow-card overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4">
              <div className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${list.active ? 'bg-success' : 'bg-muted-foreground'}`} />
                <div>
                  <p className="font-600 text-foreground">{list.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-2xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-600">{list.zone.replace(/_/g, ' ')}</span>
                    <span className="text-2xs bg-accent/10 text-accent px-2 py-0.5 rounded-full font-600">{list.tier}</span>
                    <span className="text-2xs text-muted-foreground">{list.products.length} {t('pricelists.products')}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => toggleActive(list.id)} className={`text-2xs font-600 px-2.5 py-1 rounded-full transition-colors ${list.active ? 'bg-success/10 text-success hover:bg-success/20' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}>
                  {list.active ? t('common.active') : t('common.inactive')}
                </button>
                <button onClick={() => setExpanded(expanded === list.id ? null : list.id)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors">
                  <Tag size={15} className="text-muted-foreground" />
                </button>
                <button onClick={() => deleteList(list.id)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-danger/10 transition-colors">
                  <Trash2 size={15} className="text-danger" />
                </button>
              </div>
            </div>
            {expanded === list.id && list.products.length > 0 && (
              <div className="border-t border-border px-5 py-3">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-muted-foreground text-xs">
                      <th className={`pb-2 font-600 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>{t('po.product')}</th>
                      <th className="pb-2 font-600 text-center">{language === 'ar' ? 'السعر الأساسي' : 'Prix de base'}</th>
                      <th className="pb-2 font-600 text-center">{language === 'ar' ? 'السعر المخصص' : 'Prix personnalisé'}</th>
                      <th className="pb-2 font-600 text-center">{language === 'ar' ? 'الخصم' : 'Remise'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {list.products.map(p => (
                      <tr key={p.productId}>
                        <td className="py-2 font-500">{p.productName}</td>
                        <td className="py-2 text-center font-tabular text-muted-foreground">{p.basePrice} {t('common.mad')}</td>
                        <td className="py-2 text-center font-tabular font-600 text-primary">{p.customPrice} {t('common.mad')}</td>
                        <td className="py-2 text-center">
                          <span className="text-2xs font-700 text-success bg-success/10 px-2 py-0.5 rounded-full">
                            -{Math.round((1 - p.customPrice / p.basePrice) * 100)}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
